import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  licenseApiService,
  parseLicenseRequestPayload,
  type LicenseRequest,
  type IssuedLicense,
  type IssuedLicenseRecord,
} from '@/services/licenseApiService'
import { HttpError } from '@/lib/httpError'
import { useLicenseIssuer } from '@/hooks/useLicenseIssuer'
import IssuanceCodeModal from '@/components/modals/IssuanceCodeModal'

type LicenseType = 'trial' | 'standard' | 'enterprise'

// The quota each type carries unless it is overridden. Shown next to the choice
// so nobody has to issue a license to find out what it grants.
const DEFAULT_FLOWS: Record<LicenseType, number> = {
  trial: 3,
  standard: 25,
  enterprise: 500,
}

// How long each type runs unless someone changes it.
//
// Nothing in the signing path defines this: a signed key gets whatever number
// it is given, and license-cli defaults every type to 365. That default is a
// trap on a form, because picking "trial" and pressing Issue would produce a
// trial license lasting a year.
//
// Seven days for trial matches the trial the operator grants an installation
// with no key at all, so the word means the same thing in both places and
// nobody has to ask which trial is meant.
const DEFAULT_DAYS: Record<LicenseType, number> = {
  trial: 7,
  standard: 365,
  enterprise: 365,
}

// Whether this installation may issue at all comes from useLicenseIssuer, which
// asks the server once and shares the answer with the sidebar entry that leads
// here. Deciding it twice is how a menu item and the page behind it end up
// disagreeing.
export default function IssueLicensePage() {
  const { t } = useTranslation(['plans', 'common'])
  const navigate = useNavigate()
  const { canIssue: allowed, needsTOTP } = useLicenseIssuer()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [customerName, setCustomerName] = useState('')
  const [email, setEmail] = useState('')
  const [organization, setOrganization] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [licenseType, setLicenseType] = useState<LicenseType>('enterprise')
  const [validDays, setValidDays] = useState(DEFAULT_DAYS.enterprise)
  const [maxFlows, setMaxFlows] = useState('')

  // Set when this issuance replaces a license the customer lost. It is a link,
  // not a revocation: the old key still works until it expires, and the ledger
  // says which one this stands in for so two rows for one customer do not read
  // as two licenses in the wild.
  const [replacesJti, setReplacesJti] = useState('')

  // Who this customer writes to about renewal. Recorded per license, because
  // the person who sold it is often not the person who runs the platform.
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')

  // The customer request this issuance answers, when opened from Platform
  // Requests (BAFTT-12). Issuing closes it with the license reference.
  const [searchParams, setSearchParams] = useSearchParams()
  const requestId = searchParams.get('request') ?? ''
  const [fromRequest, setFromRequest] = useState<LicenseRequest | null>(null)
  const [requestError, setRequestError] = useState('')
  const [requestClosed, setRequestClosed] = useState<boolean | null>(null)

  // The minted key, held only in this page's state. The server records the
  // issuance but not the key, so once this page is left the key is gone and the
  // customer needs a re-issue.
  const [issued, setIssued] = useState<IssuedLicense | null>(null)
  const [copied, setCopied] = useState(false)
  const [ledger, setLedger] = useState<IssuedLicenseRecord[]>([])

  // Delivery. canSend comes from the server, because an installation can be
  // able to sign and not able to send; offering a step that answers 501 is
  // worse than not offering it.
  const [canSend, setCanSend] = useState(false)
  const [sendTo, setSendTo] = useState('')
  const [sendState, setSendState] = useState<'ask' | 'sending' | 'sent' | 'declined'>('ask')
  const [sendError, setSendError] = useState('')

  // Signing and mailing each need a code from the authenticator app confirmed
  // in the last few minutes (BAFTT-13). The server is the judge; this copy of
  // the deadline only spares a round trip that would be refused anyway.
  const [confirmedUntil, setConfirmedUntil] = useState<Date | null>(null)
  const [codeFor, setCodeFor] = useState<'issue' | 'send' | null>(null)
  const isConfirmed = () =>
    confirmedUntil !== null && confirmedUntil.getTime() - Date.now() > 5000
  const isStepUpRequired = (err: unknown) =>
    err instanceof HttpError && err.code === 'STEP_UP_REQUIRED'

  // The ledger is context, not the job. A form that refuses to open because a
  // list failed would be worse than one without the list.
  const loadLedger = async () => {
    try {
      setLedger((await licenseApiService.listIssued()) ?? [])
    } catch {
      setLedger([])
    }
  }

  useEffect(() => {
    if (allowed && !needsTOTP) void loadLedger()
  }, [allowed, needsTOTP])

  // Fill the form from the request. Everything stays editable: the request is
  // what the customer asked for, the agreement decides what they get.
  useEffect(() => {
    if (!allowed || needsTOTP || !requestId) return
    let cancelled = false
    licenseApiService
      .getLicenseRequest(requestId)
      .then((request) => {
        if (cancelled) return
        if (request.status !== 'pending') {
          setRequestError(t('issueModal.fromRequest.answered', 'This request has already been answered.'))
          return
        }
        const p = parseLicenseRequestPayload(request.payload)
        if (!p) return
        setFromRequest(request)
        setRequestError('')
        setCustomerName(p.customer_name)
        setEmail(p.email)
        setOrganization(p.organization || '')
        setLicenseType(p.license_type)
        setValidDays(p.valid_days)
        setMaxFlows(p.max_flows ? String(p.max_flows) : '')
      })
      .catch((err) => {
        if (!cancelled) setRequestError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [allowed, needsTOTP, requestId])

  const detachRequest = () => {
    setFromRequest(null)
    setRequestError('')
    setSearchParams({}, { replace: true })
  }

  // Changing the type moves the validity with it. The alternative, keeping a
  // number the reader last saw next to a different type, is how a one-year
  // trial gets issued by accident. Anyone who wants a different length still
  // types it after choosing the type, which is the order people work in.
  const handleTypeChange = (next: LicenseType) => {
    setLicenseType(next)
    setValidDays(DEFAULT_DAYS[next])
  }

  const handleIssue = () => {
    if (!customerName.trim() || !email.trim()) {
      setError(t('issueModal.errors.required'))
      return
    }
    if (!isConfirmed()) {
      setCodeFor('issue')
      return
    }
    void issue()
  }

  const issue = async () => {
    try {
      setLoading(true)
      setError('')

      const result = await licenseApiService.issueLicense({
        customer_name: customerName.trim(),
        email: email.trim(),
        organization: organization.trim() || undefined,
        customer_id: customerId.trim() || undefined,
        license_type: licenseType,
        valid_days: validDays,
        max_flows: maxFlows.trim() ? Number(maxFlows) : undefined,
        replaces_jti: replacesJti || undefined,
        contact_name: contactName.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        request_id: fromRequest?.id,
      })

      if (result === null) {
        setError(t('issueModal.errors.notAvailable'))
        return
      }

      setIssued(result.license)
      setRequestClosed(fromRequest ? result.request_closed !== false : null)
      setCanSend(result.can_send)
      setSendTo(result.license.email)
      setSendState('ask')
      setSendError('')
      void loadLedger()
    } catch (err) {
      if (isStepUpRequired(err)) {
        setConfirmedUntil(null)
        setCodeFor('issue')
        return
      }
      setError(
        err instanceof HttpError || err instanceof Error
          ? err.message
          : t('issueModal.errors.generic')
      )
    } finally {
      setLoading(false)
    }
  }

  // Fill the form from a ledger row. Everything the customer already had is
  // carried over except the dates, which are what a re-issue is for.
  const handleReissue = (row: IssuedLicenseRecord) => {
    setCustomerName(row.customer_name)
    setEmail(row.customer_email)
    setOrganization(row.organization || '')
    setCustomerId(row.customer_id || '')
    const type = (['trial', 'standard', 'enterprise'] as const).find((x) => x === row.license_type)
    const next = type || 'standard'
    setLicenseType(next)
    setValidDays(DEFAULT_DAYS[next])
    // The quota is carried only when it was not the type's own default, so a
    // re-issue does not silently pin a number the customer never negotiated.
    setMaxFlows(row.max_flows && row.max_flows !== DEFAULT_FLOWS[next] ? String(row.max_flows) : '')
    setReplacesJti(row.jti)
    setContactName(row.contact_name || '')
    setContactEmail(row.contact_email || '')
    setError('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSend = () => {
    if (!issued) return
    if (!isConfirmed()) {
      setCodeFor('send')
      return
    }
    void send()
  }

  const send = async () => {
    if (!issued) return
    try {
      setSendState('sending')
      setSendError('')
      const res = await licenseApiService.sendLicense(issued.jti, issued.license_key, sendTo.trim())
      setSendTo(res.sent_to)
      setSendState('sent')
      void loadLedger()
    } catch (err) {
      // Back to the question, not to a dead end: the key is still on screen,
      // so trying again or copying it by hand are both still open.
      setSendState('ask')
      if (isStepUpRequired(err)) {
        setConfirmedUntil(null)
        setCodeFor('send')
        return
      }
      setSendError(
        err instanceof HttpError || err instanceof Error
          ? err.message
          : t('issueModal.send.failed')
      )
    }
  }

  const handleCopy = async () => {
    if (!issued) return
    try {
      await navigator.clipboard.writeText(issued.license_key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError(t('issueModal.errors.copyFailed'))
    }
  }

  const handleDownload = () => {
    if (!issued) return
    const blob = new Blob([JSON.stringify(issued, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `synstream-license-${issued.jti}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
  }

  const handleDone = () => navigate('/plans')

  const handleConfirmed = (until: Date) => {
    setConfirmedUntil(until)
    const next = codeFor
    setCodeFor(null)
    if (next === 'issue') void issue()
    if (next === 'send') void send()
  }

  // Nothing is drawn until the server has answered whether this installation can
  // issue at all. Drawing the form first and taking it away is worse than a beat
  // of nothing.
  if (allowed === null) return null

  if (allowed === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h1 className="text-xl font-semibold text-text-dark">{t('issueModal.title')}</h1>
          <p className="text-sm text-gray-600 mt-2">{t('issueModal.errors.notAvailable')}</p>
          <button
            onClick={handleDone}
            className="mt-4 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('issuePage.back')}
          </button>
        </div>
      </div>
    )
  }

  // A vendor account with no second factor. Said plainly, with the way to fix
  // it, rather than drawing a form every button of which the server refuses.
  if (needsTOTP) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
          <h1 className="text-xl font-semibold text-amber-900">
            {t('issueModal.totpRequired.title', 'Two-factor authentication is required to issue licenses')}
          </h1>
          <p className="text-sm text-amber-800 mt-2">
            {t(
              'issueModal.totpRequired.body',
              'A signed license unlocks a customer installation, so a password alone is not enough here. Set up an authenticator app in your profile, then come back.'
            )}
          </p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => navigate('/settings/profile?reason=issuance_totp')}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('issueModal.totpRequired.action', 'Set up two-factor')}
            </button>
            <button
              onClick={handleDone}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-white transition-colors"
            >
              {t('issuePage.back')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <IssuanceCodeModal
        open={codeFor !== null}
        action={codeFor ?? 'issue'}
        onConfirmed={handleConfirmed}
        onCancel={() => setCodeFor(null)}
      />
      <button
        onClick={handleDone}
        className="text-sm text-gray-600 hover:text-gray-900 transition-colors mb-4 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span>{t('issuePage.back')}</span>
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text-dark">{t('issueModal.title')}</h1>
        <p className="text-gray-600 mt-2">{t('issueModal.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* The minted key. Shown once, and said so plainly. */}
          {issued ? (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-900">
                  {t('issueModal.result.title', { name: issued.customer_name })}
                </p>
                <p className="text-xs text-green-800 mt-1">
                  {t('issueModal.result.expires', { date: formatDate(issued.expires_at) })}
                </p>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                <p className="text-sm text-amber-900">{t('issueModal.result.onlyCopy')}</p>
              </div>

              {requestClosed === true && (
                <p className="text-sm text-green-800">
                  {t('issueModal.fromRequest.closed', 'The customer request is marked as issued, with this reference.')}
                </p>
              )}
              {requestClosed === false && (
                <p className="text-sm text-amber-900">
                  {t('issueModal.fromRequest.notClosed', 'The key is valid, but the customer request is still open. Give the customer the reference directly.')}
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('issueModal.result.keyLabel')}
                </label>
                <textarea
                  readOnly
                  value={issued.license_key}
                  rows={5}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs break-all"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {copied ? t('issueModal.result.copied') : t('issueModal.result.copy')}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('issueModal.result.download')}
                  </button>
                </div>
              </div>

              {/* Delivery. Asked rather than assumed: some customers cannot
                  accept a credential by email at all, and the address on the
                  license is not always the person who installs it. */}
              {canSend && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  {sendState === 'sent' ? (
                    <p className="text-sm text-green-800">
                      {t('issueModal.send.sent', { email: sendTo })}
                    </p>
                  ) : sendState === 'declined' ? (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-gray-600">{t('issueModal.send.declined')}</p>
                      <button
                        onClick={() => setSendState('ask')}
                        className="text-sm text-primary hover:underline shrink-0"
                      >
                        {t('issueModal.send.reopen')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900">
                        {t('issueModal.send.question')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('issueModal.send.hint')}
                      </p>
                      <label className="block text-xs font-medium text-gray-700 mt-3 mb-1">
                        {t('issueModal.send.toLabel')}
                      </label>
                      <input
                        type="email"
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        disabled={sendState === 'sending'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      {sendError && (
                        <p className="text-sm text-red-700 mt-2">{sendError}</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={handleSend}
                          disabled={sendState === 'sending' || !sendTo.trim()}
                          className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
                        >
                          {sendState === 'sending'
                            ? t('issueModal.send.sending')
                            : t('issueModal.send.yes')}
                        </button>
                        <button
                          onClick={() => setSendState('declined')}
                          disabled={sendState === 'sending'}
                          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {t('issueModal.send.no')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <dl className="text-sm text-gray-600 grid grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-gray-200">
                <dt className="font-medium text-gray-700">{t('issueModal.fields.licenseType')}</dt>
                <dd>{issued.license_type}</dd>
                <dt className="font-medium text-gray-700">{t('issueModal.fields.maxFlows')}</dt>
                <dd>{issued.max_flows}</dd>
                <dt className="font-medium text-gray-700">{t('issueModal.result.reference')}</dt>
                <dd className="font-mono font-semibold text-gray-900">{issued.reference || '-'}</dd>
                <dt className="font-medium text-gray-700">{t('issueModal.result.id')}</dt>
                <dd className="font-mono text-xs break-all">{issued.jti}</dd>
              </dl>
            </>
          ) : (
            <>
              {requestError && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3">
                  <p className="text-sm text-amber-900">{requestError}</p>
                  <button onClick={detachRequest} className="text-sm text-amber-900 hover:underline shrink-0">
                    {t('issueModal.fromRequest.clear', 'Issue without it')}
                  </button>
                </div>
              )}

              {fromRequest && (() => {
                const p = parseLicenseRequestPayload(fromRequest.payload)
                return (
                  <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-green-900">
                        {t('issueModal.fromRequest.banner', {
                          account: fromRequest.requestor?.account ?? '-',
                          defaultValue: 'Answering a license request from {{account}}. Issuing closes it.',
                        })}
                      </p>
                      {p?.note && <p className="text-xs text-green-800 mt-1 whitespace-pre-wrap break-words">{p.note}</p>}
                    </div>
                    <button onClick={detachRequest} className="text-sm text-green-900 hover:underline shrink-0">
                      {t('issueModal.fromRequest.clear', 'Issue without it')}
                    </button>
                  </div>
                )
              })()}

              {replacesJti && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-blue-900">{t('issueModal.reissue.banner')}</p>
                    <p className="text-xs text-blue-800 mt-1 font-mono break-all">{replacesJti}</p>
                    <p className="text-xs text-blue-800 mt-1">{t('issueModal.reissue.note')}</p>
                  </div>
                  <button
                    onClick={() => setReplacesJti('')}
                    className="text-sm text-blue-900 hover:underline shrink-0"
                  >
                    {t('issueModal.reissue.clear')}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.customerName')} *
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.email')} *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.organization')}
                  </label>
                  <input
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.customerId')}
                  </label>
                  <input
                    type="text"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    disabled={loading}
                    placeholder={t('issueModal.fields.customerIdHint')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.contactName')}
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    disabled={loading}
                    placeholder={t('issueModal.fields.contactHint')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.contactEmail')}
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.licenseType')}
                  </label>
                  <select
                    value={licenseType}
                    onChange={(e) => handleTypeChange(e.target.value as LicenseType)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="trial">{t('issueModal.types.trial')}</option>
                    <option value="standard">{t('issueModal.types.standard')}</option>
                    <option value="enterprise">{t('issueModal.types.enterprise')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.validDays')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={validDays}
                    onChange={(e) => setValidDays(Number(e.target.value))}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.maxFlows')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={maxFlows}
                    onChange={(e) => setMaxFlows(e.target.value)}
                    disabled={loading}
                    placeholder={String(DEFAULT_FLOWS[licenseType])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {ledger.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('issueModal.ledger.title')}
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {ledger.slice(0, 20).map((row) => (
                      <div key={row.jti} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                        <span className="text-gray-900 truncate">
                          {row.reference && (
                            <span className="font-mono text-gray-500 mr-2">{row.reference}</span>
                          )}
                          {row.customer_name} <span className="text-gray-500">{row.customer_email}</span>
                        </span>
                        <span className="flex items-center gap-3 shrink-0">
                          <span className="text-gray-500">
                            {row.license_type}, {t('issueModal.ledger.until')} {formatDate(row.expires_at)}
                          </span>
                          <button
                            onClick={() => handleReissue(row)}
                            className="text-primary hover:underline"
                            title={t('issueModal.reissue.title')}
                          >
                            {t('issueModal.reissue.action')}
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleDone}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            {issued ? t('common:close', 'Close') : t('common:cancel', 'Cancel')}
          </button>
          {!issued && (
            <button
              onClick={handleIssue}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? t('issueModal.actions.issuing') : t('issueModal.actions.issue')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
